/**
 * CalendarGrid.js
 * 版本：V1.03 | 更新日期：2025-07-19
 * 功能：日曆網格組件，顯示月份日曆和預約資訊
 * 關連檔案：App.js
 * 
 * 更新內容：
 * V1.03 (2025-07-19) - 今天日期框特殊標示
 * - 為今天的日期框加上橙色邊框和背景色（細邊框不影響數字顯示）
 * - 今天的日期數字使用橙色和粗體顯示
 * - 讓用戶能清楚識別今天在日曆中的位置
 * 
 * V1.02 (2025-07-19) - 整合區域權限系統
 * - 根據夥伴區域權限篩選日曆顯示的預約
 * - 支援向後相容性（無權限設定的夥伴可看到所有預約）
 * - 確保夥伴只能看到有權限的區域預約
 * 
 * V1.01 (2025-07-16) - 初始版本
 * - 基本日曆網格顯示功能
 * - 月份導航和預約資訊顯示
 */

import React, { useMemo } from 'react';
// 【AI新增】區域權限系統導入
// Why: 根據夥伴權限篩選日曆顯示的預約
import { isAdmin } from '../../utils/roleUtils';
import { getMarketRegion } from '../../utils/marketUtils';

const CalendarGrid = ({ currentDate, setCurrentDate, bookings, onDayClick, appId, db, currentUser, functions, markets }) => {
    const startOfMonth = useMemo(() => new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), [currentDate]);
    const endOfMonth = useMemo(() => new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0), [currentDate]);
    const startDay = useMemo(() => startOfMonth.getDay(), [startOfMonth]);
    const daysInMonth = useMemo(() => endOfMonth.getDate(), [endOfMonth]);
    const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    
    // 【AI新增】根據夥伴區域權限篩選預約
    // Why: 確保夥伴只能看到有權限的區域預約
    const filteredBookings = useMemo(() => {
        // 【AI修正】權限過濾防呆：
        // Why: 避免因資料異常（如市場無 region、夥伴無 regions）導致全部預約都看不見
        if (isAdmin(currentUser)) return bookings;
        if (!currentUser || !Array.isArray(currentUser.regions) || currentUser.regions.length === 0) {
            return bookings;
        }
        return bookings.filter(booking => {
            const market = markets?.find(m => m.id === booking.marketId);
            if (!market) return true; // 找不到市場，預設顯示
            const marketRegion = getMarketRegion(market);
            if (!marketRegion) return true; // 找不到區域，預設顯示
            return currentUser.regions.includes(marketRegion);
        });
    }, [bookings, currentUser, markets]);
    
    return (
        <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
                <button onClick={prevMonth} className="p-2 rounded-full hover:bg-gray-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h2 className="text-lg font-bold text-gray-800">{currentDate.getFullYear()} 年 {currentDate.getMonth() + 1} 月</h2>
                <button onClick={nextMonth} className="p-2 rounded-full hover:bg-gray-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-500 mb-1">
                {['日', '一', '二', '三', '四', '五', '六'].map(day => <div key={day} className="py-1">{day}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: startDay }).map((_, i) => <div key={`empty-${i}`}></div>)}
                {Array.from({ length: daysInMonth }).map((_, day) => {
                    const dayNumber = day + 1;
                    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const dayOfMonth = String(date.getDate()).padStart(2, '0');
                    const dateStr = `${year}-${month}-${dayOfMonth}`;
                    const dayBookings = filteredBookings.filter(b => b.date === dateStr);
                    
                    // 【AI新增】檢查是否為今天
                    // Why: 為今天的日期框加上特殊顏色，讓用戶能清楚識別今天的位置
                    const today = new Date();
                    const isToday = 
                        date.getFullYear() === today.getFullYear() &&
                        date.getMonth() === today.getMonth() &&
                        date.getDate() === today.getDate();
                    
                    // 【AI新增】今天的日期框使用特殊樣式
                    // Why: 使用橙色主題突出今天的日期，讓用戶一眼就能看到今天的位置
                    const todayStyles = isToday 
                        ? "border border-orange-400 bg-orange-50 shadow-md" 
                        : "border border-gray-200";
                    
                    return (
                        <div 
                            key={dayNumber} 
                            onClick={() => onDayClick(dateStr)} 
                            className={`h-20 sm:h-24 rounded-md p-1 flex flex-col cursor-pointer hover:bg-blue-50 transition-colors ${todayStyles}`}
                        >
                            <span className={`font-medium text-sm self-center sm:self-start ${isToday ? 'text-orange-700 font-bold' : ''}`}>
                                {dayNumber}
                            </span>
                            <div className="flex-grow overflow-hidden text-xs space-y-0.5 mt-1">
                                {dayBookings.map(b => (
                                    <div key={b.id} className="px-1 rounded bg-green-100 text-green-800 font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                                        {b.marketName.substring(0, 2)}{b.remark && '*'}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarGrid;
